'use client';

import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { AlertCircle, Home, RotateCw } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error);
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <Logo size="md" />
        </div>

        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="font-display text-display">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            We apologize for the inconvenience. An unexpected error occurred. Try
            again, or contact support if the problem persists.
          </p>
        </div>

        <div className="space-y-2 pt-4">
          <Button
            onClick={() => reset()}
            size="lg"
            className="gap-2 w-full"
          >
            <RotateCw className="w-4 h-4" />
            Try again
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="gap-2 w-full"
            onClick={() => window.location.href = '/'}
          >
            <Home className="w-4 h-4" />
            Back to home
          </Button>
        </div>

        <div className="text-sm text-muted-foreground bg-muted p-3 rounded-xl">
          <p className="font-mono text-xs break-words tabular-nums">
            Error ID: {error.digest || 'unknown'}
          </p>
        </div>

        <div className="text-sm text-muted-foreground">
          <p>Need help? <a href="mailto:support@notiia.com" className="text-foreground hover:text-speaker-1 underline underline-offset-2">Contact support</a></p>
        </div>
      </div>
    </div>
  );
}
